import { noop } from "lodash-es";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useTypedContext from "hooks/useTypedContext";
import useHasSpaceAdminAccess from "shared/Space/useHasSpaceAdminAccess";
import { isLegacySpace } from "utils/space";
import { TreeNode } from "utils/tree";
import { SpacesViewActionsContext } from "views/Account/Spaces/ActionsContext";
import { showSpaceDrawer } from "views/Account/Spaces/SpacesDrawer";
import { SpaceWithParentAccessLevel, SpacesDrawerWriteMode } from "views/Account/Spaces/types";
import { isPredefinedSpace } from "views/Account/Spaces/utils";

import { SpacesListContext } from "../Context";

const NON_ADMIN_SPACE_MANAGEMENT_MESSAGE = "Space management is restricted to account admins.";

const SpacesListRowActionsCell = ({
  item: node,
  ...cellProps
}: TableSimpleCellProps<TreeNode<SpaceWithParentAccessLevel>>) => {
  const { onCreate, onDelete } = useTypedContext(SpacesViewActionsContext);
  const { isRootAdmin, canNonRootAdminCreateSpace, expandedKeys, onToggle } =
    useTypedContext(SpacesListContext);

  const hasAccessToParentSpace = useHasSpaceAdminAccess(node.item.parentSpace || undefined);
  const hasAccessToSpace = useHasSpaceAdminAccess(node.item.id);

  // Both actions are always shown; when blocked they're disabled with an explanation rather than
  // hidden (matches the app-wide access pattern). Permission denials come first, then space rules.
  const getAddChildSpaceDisabledReason = () => {
    if (!isRootAdmin && !canNonRootAdminCreateSpace) {
      return NON_ADMIN_SPACE_MANAGEMENT_MESSAGE;
    }

    if (!isRootAdmin && !hasAccessToSpace) {
      return "Requires admin access to this space.";
    }

    if (isLegacySpace(node.id)) {
      return "Legacy spaces can't have child spaces.";
    }

    return undefined;
  };

  const getEditDisabledReason = () => {
    if (!isRootAdmin && !canNonRootAdminCreateSpace) {
      return NON_ADMIN_SPACE_MANAGEMENT_MESSAGE;
    }

    if (!isRootAdmin && !hasAccessToParentSpace) {
      return "Requires admin access to the parent space.";
    }

    if (isPredefinedSpace(node.id)) {
      return "Predefined spaces can't be edited.";
    }

    return undefined;
  };

  const getDeleteDisabledReason = () => {
    if (!isRootAdmin && !canNonRootAdminCreateSpace) {
      return NON_ADMIN_SPACE_MANAGEMENT_MESSAGE;
    }

    if (!isRootAdmin && !hasAccessToParentSpace) {
      return "Requires admin access to the parent space.";
    }

    if (isPredefinedSpace(node.id)) {
      return "Predefined spaces can't be deleted.";
    }

    if (node.children.length > 0) {
      return "Delete this space's child spaces first.";
    }

    return undefined;
  };

  const addChildSpaceDisabledReason = getAddChildSpaceDisabledReason();
  const editDisabledReason = getEditDisabledReason();
  const deleteDisabledReason = getDeleteDisabledReason();

  const handleCreate = () => {
    onCreate(node.id);
    if (!expandedKeys.has(node.id)) {
      onToggle(node.id);
    }
  };

  const handleSelect = () => {
    showSpaceDrawer({
      space: node.item,
      drawerWriteMode: SpacesDrawerWriteMode.Edit,
      isRootAdmin,
      onClose: noop,
    });
  };

  return (
    <TableSimpleCell {...cellProps}>
      <DropdownMenuEllipsis tooltip="Space actions">
        <DropdownMenuItem
          onAction={handleCreate}
          isDisabled={!!addChildSpaceDisabledReason}
          tooltip={addChildSpaceDisabledReason}
          tooltipPlacement="left"
        >
          Add child space
        </DropdownMenuItem>

        <DropdownMenuItem
          onAction={handleSelect}
          isDisabled={!!editDisabledReason}
          tooltip={editDisabledReason}
          tooltipPlacement="left"
        >
          Edit details
        </DropdownMenuItem>

        <CopyFieldDropdownMenuItem title="Copy ID" value={node.id} />

        <DropdownMenuItem
          onAction={() => onDelete(node.id)}
          danger
          isDisabled={!!deleteDisabledReason}
          tooltip={deleteDisabledReason}
          tooltipPlacement="left"
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenuEllipsis>
    </TableSimpleCell>
  );
};

export default SpacesListRowActionsCell;
